import React, { useState, useEffect } from 'react';
import { MessageSquare, CheckCircle, Clock, X } from 'lucide-react';
import { feedbackAPI } from '../utils/api';
import { CountryBadge } from '../components/SearchableCountrySelect';

const FeedbackManagement = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  
  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const res = await feedbackAPI.getFeedback();
      if (res.data.success) {
        setFeedback(res.data.data || []);
        if (selectedItem) {
          const updated = (res.data.data || []).find(f => f.id === selectedItem.id);
          setSelectedItem(updated || null);
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (id, status) => {
    try {
      const res = await feedbackAPI.resolveFeedback(id, status);
      if (res.data.success) {
        fetchFeedback();
      }
    } catch (err) {
      alert("Failed to update status");
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div>
        <h2 className="text-3xl font-extrabold text-slate-800">App Feedback</h2>
        <p className="text-slate-500 mt-1">Review bug reports and general app feedback submitted by users.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Feedback List */}
        <div className="lg:col-span-1 bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden flex flex-col max-h-[600px]">
          <div className="p-5 border-b border-slate-50 flex items-center justify-between">
            <h3 className="font-bold text-slate-800">Feedback Inbox</h3>
            <button onClick={fetchFeedback} className="text-xs text-pink-600 font-bold hover:underline">Refresh</button>
          </div>

          {loading ? (
            <div className="p-10 text-center flex-1">
              <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            </div>
          ) : feedback.length === 0 ? (
            <div className="p-10 text-center text-slate-400 flex-1">
              <MessageSquare size={36} className="mx-auto mb-2 opacity-35" />
              <p className="text-sm">No feedback received.</p>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 divide-y divide-slate-50">
              {feedback.map(item => (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedItem(item)}
                  className={`p-5 cursor-pointer hover:bg-slate-50/80 transition relative ${
                    selectedItem?.id === item.id ? 'bg-pink-50/50 border-r-4 border-pink-500' : ''
                  }`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      item.status === 'resolved' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'
                    }`}>
                      {item.status || 'pending'}
                    </span>
                  </div>
                  <h4 className="font-bold text-slate-800 text-sm mt-2 line-clamp-1">{item.subject}</h4>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.message}</p>
                  <div className="flex justify-between items-center mt-3 text-[10px] text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <span>{item.user_name}</span>
                      {item.country && <CountryBadge country={item.country} className="text-[10px] px-1.5 py-0.5" />}
                    </div>
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Feedback Details */}
        <div className="lg:col-span-2 flex flex-col bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/30 overflow-hidden h-[600px]">
          {selectedItem ? (
            <div className="flex flex-col h-full">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white">
                <div>
                  <h3 className="font-bold text-slate-800 text-xl">{selectedItem.subject}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-slate-500">Submitted by: {selectedItem.user_name} ({selectedItem.email})</p>
                    {selectedItem.country && <CountryBadge country={selectedItem.country} />}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectedItem.status !== 'resolved' && (
                    <button 
                      onClick={() => handleResolve(selectedItem.id, 'resolved')}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 active:scale-95 text-white font-bold rounded-xl text-sm transition"
                    >
                      Mark Resolved
                    </button>
                  )}
                  <button onClick={() => setSelectedItem(null)} className="p-2 text-slate-400 hover:bg-slate-100 rounded-lg">
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto bg-slate-50">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                  <h4 className="font-bold text-slate-700 mb-4">Message:</h4>
                  <p className="text-slate-600 whitespace-pre-wrap">{selectedItem.message}</p>
                  
                  {selectedItem.image_url && (
                    <div className="mt-6 border-t border-slate-100 pt-6">
                      <h4 className="font-bold text-slate-700 mb-4">Attached Image:</h4>
                      <img src={selectedItem.image_url} alt="Feedback Attachment" className="max-w-md rounded-xl border border-slate-200 shadow-sm" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-8">
              <MessageSquare size={54} className="opacity-20 mb-3" />
              <h4 className="font-extrabold text-slate-600 text-lg">No Feedback Selected</h4>
              <p className="text-sm mt-1 max-w-sm">Select an item from the left panel to review the feedback details.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeedbackManagement;
